/**
 * The site-wide three-dot menu that every row or card carries, so per-entry actions have one home as
 * they accumulate. The popover renders in a portal, fixed to the button, so a clipping table or card
 * (overflow hidden for rounded corners) can never crop it; it flips above when the viewport runs out.
 * Its coordinates land as CSS custom properties on the element, which the class consumes. An entry with
 * `items` opens them beneath itself in place, so a choice of several stays one menu.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Chevron, Icon, Tick } from './icons';

export interface RowMenuItem {
  /** The entry's name; also its accessible name where `content` draws it instead. */
  label: string;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Drawn in place of the label. */
  content?: ReactNode;
  /** A tick marks the current choice. */
  checked?: boolean;
  /** Choices this entry opens onto. */
  items?: RowMenuItem[];
}

const POP_GAP_PX = 4;
const VIEWPORT_PAD_PX = 8;

export function RowMenu({ items, title }: { items: (RowMenuItem | null)[]; title?: string }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const btn = btnRef.current;
      const pop = popRef.current;
      if (!btn || !pop) return;
      const r = btn.getBoundingClientRect();
      const popH = pop.offsetHeight;
      const popW = pop.offsetWidth;
      const below = r.bottom + POP_GAP_PX + popH <= window.innerHeight - VIEWPORT_PAD_PX;
      const top = below ? r.bottom + POP_GAP_PX : Math.max(VIEWPORT_PAD_PX, r.top - POP_GAP_PX - popH);
      const left = Math.max(VIEWPORT_PAD_PX, r.right - popW);
      pop.style.setProperty('--pop-x', `${Math.round(left)}px`);
      pop.style.setProperty('--pop-y', `${Math.round(top)}px`);
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, expanded]);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);
    window.addEventListener('pointerdown', close);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  return (
    <div className="row-menu">
      <button ref={btnRef} type="button" className={`row-menu-btn${open ? ' on' : ''}`} title={title ?? 'Actions'} aria-label={title ?? 'Actions'} aria-expanded={open}
        onClick={() => { setOpen(!open); setExpanded(null); }}>
        <Icon name="dots" />
      </button>
      {open && createPortal(
        <div className="row-menu-pop" role="menu" ref={popRef}>
          {items.map((item, i) => item === null ? (
            <div key={`d${i}`} className="row-menu-hr" />
          ) : item.items ? (
            <div key={item.label} className="row-menu-group">
              <button type="button" role="menuitem" className="row-menu-item row-menu-parent" disabled={item.disabled} aria-expanded={expanded === item.label}
                onClick={() => setExpanded(expanded === item.label ? null : item.label)}>
                {item.content ?? item.label}<Chevron up={expanded === item.label} />
              </button>
              {expanded === item.label && item.items.map((sub) => (
                <MenuEntry key={sub.label} item={sub} sub onPick={() => setOpen(false)} />
              ))}
            </div>
          ) : (
            <MenuEntry key={item.label} item={item} onPick={() => setOpen(false)} />
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function MenuEntry({ item, sub, onPick }: { item: RowMenuItem; sub?: boolean; onPick: () => void }) {
  return (
    <button type="button" role="menuitem" className={`row-menu-item${sub ? ' sub' : ''}${item.danger ? ' danger' : ''}`} disabled={item.disabled}
      aria-label={item.content ? item.label : undefined} aria-current={item.checked || undefined}
      onClick={() => { onPick(); item.onClick?.(); }}>
      {item.content ?? item.label}{item.checked && <Tick />}
    </button>
  );
}
